import React, { useState } from "react";
import { addRoomType } from "../../apis/room";

type AddRoomTypeProps = {
  open: boolean;
  onClose: () => void;
};

type RoomTypeErrors = {
  name?: string;
  price_per_hour?: string;
  price_per_day?: string;
};

const AddRoomType: React.FC<AddRoomTypeProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    price_per_hour: "",
    price_per_day: "",
  });

  const [errors, setErrors] = useState<RoomTypeErrors>({});
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  // Validation function
  const validateForm = () => {
    const e: RoomTypeErrors = {};
    if (!formData.name.trim()) e.name = "* Room type name is required";
    if (!formData.price_per_hour) e.price_per_hour = "* Price per hour is required";
    if (!formData.price_per_day) e.price_per_day = "* Price per day is required";
    return { hasErrors: Object.keys(e).length > 0, errors: e };
  };

  // Save handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { hasErrors, errors } = validateForm();
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await addRoomType({
        name: formData.name,
        price_per_hour: Number(formData.price_per_hour),
        price_per_day: Number(formData.price_per_day),
      });

      handleClose(true); 
    } catch (err: any) {
      setErrors({ name: err?.response?.data?.message || "Failed to add room type" });
    } finally {
      setLoading(false);
    }
  };

  // Close handler
  const handleClose = (forceClose = true) => {
    setFormData({ name: "", price_per_hour: "", price_per_day: "" });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary rounded-t-lg">
          <h2 className="text-white text-base font-semibold">Add Room Type</h2>
          <button onClick={() => handleClose()} type="button" className="text-white text-xl leading-none">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Room Type Name */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Room Type Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
          </div>

          {/* Prices */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-900">Price Per Hour</label>
              <input
                type="number"
                name="price_per_hour"
                value={formData.price_per_hour}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {errors.price_per_hour && <span className="text-sm text-red-500">{errors.price_per_hour}</span>}
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-900">Price Per Day</label>
              <input
                type="number"
                name="price_per_day"
                value={formData.price_per_day}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {errors.price_per_day && <span className="text-sm text-red-500">{errors.price_per_day}</span>}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-2 bg-primary hover:bg-hover text-white rounded disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => handleClose()}
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoomType;
